import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { Avatar, Icon, type IconName } from "@/components/common";
import { useAuth } from "@/auth/useAuth";
import { authService } from "@/services/authService";
import { WorkspaceSwitcher } from "./WorkspaceSwitcher";
import "./Sidebar.css";
import "./SidebarMenuStates.css";

type NavEntry = { to: string; label: string; icon: IconName; end?: boolean };

const SECTIONS: { label: string; items: NavEntry[] }[] = [
  {
    label: "Visão geral",
    items: [
      { to: "/", label: "Início", icon: "home", end: true },
      { to: "/workspaces", label: "Workspaces", icon: "building" },
      { to: "/organizacoes", label: "Organizações", icon: "layers" },
      { to: "/empresas", label: "Empresas", icon: "briefcase" },
    ],
  },
  {
    label: "Operação",
    items: [
      { to: "/operacao", label: "Operação", icon: "activity" },
      { to: "/contratos", label: "Contratos", icon: "file-text" },
      { to: "/faturamento", label: "Faturamento", icon: "credit-card" },
    ],
  },
];

function NavItem({ to, label, icon, end }: NavEntry) {
  return (
    <NavLink to={to} end={end} className={({ isActive }) => `nav-item ${isActive ? "active" : ""}`}>
      <Icon name={icon} size={18} className="nav-icon" />
      {label}
    </NavLink>
  );
}

type UserMenuItem = {
  label: string;
  icon: IconName;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
};

function UserMenu({ items, onClose }: { items: UserMenuItem[][]; onClose: () => void }) {
  return (
    <>
      <div className="user-menu-backdrop" onClick={onClose} />
      <div className="user-menu" role="menu">
        {items.map((group, gi) => (
          <div key={gi} className="user-menu-group">
            {gi > 0 && <div className="user-menu-sep" />}
            {group.map((it) => (
              <button
                key={it.label}
                type="button"
                role="menuitem"
                className={`user-menu-item ${it.danger ? "is-danger" : ""} ${it.disabled ? "is-disabled" : ""}`}
                disabled={it.disabled}
                aria-disabled={it.disabled}
                onClick={() => {
                  if (it.disabled) return;
                  onClose();
                  it.onClick();
                }}
              >
                <Icon name={it.icon} size={16} />
                {it.label}
                {it.disabled && <span className="user-menu-soon">Em breve</span>}
              </button>
            ))}
          </div>
        ))}
      </div>
    </>
  );
}

export function Sidebar() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  async function logout() {
    await authService.logout();
    qc.clear();
    navigate("/login");
  }

  const menuGroups: UserMenuItem[][] = [
    [
      { label: "Meu perfil", icon: "user", onClick: () => navigate("/preferencias") },
      { label: "Preferências", icon: "settings", onClick: () => navigate("/preferencias") },
    ],
    [
      { label: "Ajuda e suporte", icon: "help", onClick: () => undefined, disabled: true },
      { label: "Novidades", icon: "sparkles", onClick: () => undefined, disabled: true },
    ],
    [{ label: "Sair", icon: "log-out", onClick: () => void logout(), danger: true }],
  ];

  return (
    <aside className="sidebar">
      <WorkspaceSwitcher />

      <nav className="sidebar-nav">
        {SECTIONS.map((sec) => (
          <div key={sec.label}>
            <div className="nav-section-label">{sec.label}</div>
            <div className="nav-group">
              {sec.items.map((e) => (
                <NavItem key={e.to} {...e} />
              ))}
            </div>
          </div>
        ))}

        {user?.isPlatformAdmin && (
          <div>
            <div className="nav-section-label">Sistema</div>
            <div className="nav-group">
              <NavItem to="/admin" label="Admin" icon="settings" />
            </div>
          </div>
        )}
      </nav>

      <div className="sidebar-foot">
        <div className="user-menu-wrap">
          {menuOpen && <UserMenu items={menuGroups} onClose={() => setMenuOpen(false)} />}
          <button
            type="button"
            className={`user-card is-button ${menuOpen ? "open" : ""}`}
            onClick={() => setMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={menuOpen}
          >
            <Avatar name={user?.name} size="md" />
            <div className="user-info">
              <span className="user-name">{user?.name ?? "—"}</span>
              <span className="user-role">
                {user?.isPlatformAdmin ? `TDAX · ${user.platformRoleSlug ?? "admin"}` : "Membro"}
              </span>
            </div>
            <Icon name="chevrons-up-down" size={16} className="user-card-caret" />
          </button>
        </div>
      </div>
    </aside>
  );
}
